/**
 * MODULE: domain/sales-intelligence/frontend/components/SalesChatAssistant.tsx
 * PURPOSE: Main chat interface for sales intelligence with natural language queries
 * 
 * DEPENDENCIES:
 *   - react: useState, useRef, useEffect
 *   - ../hooks/useResearchWorkflow: Research workflow hook
 *   - ./MessageBubble: Message display component
 *   - ./CompanyProfileCard: Company insight display
 *   - ./TypingIndicator: Loading state indicator
 * 
 * PUBLIC API:
 *   - SalesChatAssistant: Main chat component
 * 
 * SIDE EFFECTS:
 *   - Triggers backend workflows
 *   - Manages chat message state
 * 
 * AGENT SAFETY: SAFE
 * FILE SIZE: 412 / 250 lines (exceeds limit - inline components added)
 * 
 * Recent Changes:
 * - 2026-02-07: ResearchStatusPanel z-index 20 → 30
 * - 2026-02-06: extractCompanyName fix - added "look up"/"lookup" trigger phrases; delimiter words (and, for, to, etc.) split to keep only company name; trimFillerPhrases strips leading "can you", "please", etc. Ensures e.g. "can you please look up docusign and provide a company profile" → "docusign".
 * - 2026-01-23: Fixed undefined uiComponents error - added safe array access (uiComponents || []).map() to prevent TypeError
 * - 2026-01-23: Added inline component definitions (LoadingSpinnerInline, CompanyResearchCardInline) to avoid cross-module import issues
 * - 2026-01-23: Added UI component rendering - render LoadingSpinner, CompanyResearchCard, and ErrorAlert components from SSE stream
 * 
 * See: /README.md#documentation | /ARCHITECTURE.md | /domain/sales-intelligence/README.md
 */

import React, { useState, useRef, useEffect } from 'react';
import { useResearchWorkflow, type Thought } from '../hooks/useResearchWorkflow';
import { useSellerContext } from '../hooks/useSellerContext';
import { MessageBubble } from './MessageBubble';
import { CompanyProfileCard } from './CompanyProfileCard';
import { TypingIndicator } from './TypingIndicator';
import { QuickSetupModal } from './QuickSetupModal';
import { CompanyInsight } from '../../contracts/sales-intelligence.types';

// Debug logging helper
const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development';
const debugLog = (...args: any[]) => {
  if (isDev) {
    console.log(...args);
  }
};

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  companyData?: CompanyInsight;
}

/** Delimiter words: take only the part before these (e.g. "docusign and provide..." → "docusign"). */
const COMPANY_EXTRACT_STOP_WORDS = [' and ', ' for ', ' to ', ' with ', ' about ', ' so ', ' that '];

/** Remove filler phrases from the start of an extracted string. */
function trimFillerPhrases(s: string): string {
  return s.replace(/^(?:can you|could you|please|would you)\s+/gi, '').trim();
}

/** After extraction, stop at first delimiter word and return the first part (company name). */
function takeBeforeDelimiter(extracted: string): string {
  let result = extracted.trim();
  const lower = result.toLowerCase();
  for (const word of COMPANY_EXTRACT_STOP_WORDS) {
    const index = lower.indexOf(word);
    if (index !== -1) {
      result = result.substring(0, index).trim();
      break;
    }
  }
  return result;
}

/**
 * Helper function to extract company name from natural language.
 * Handles phrases like "can you please look up docusign and provide a company profile".
 */
function extractCompanyName(input: string): string | null {
  const normalized = input.trim();
  if (!normalized) return null;

  const patterns = [
    /(?:tell me about|research|analyze|what do you know about|info on|information about|look up|lookup)\s+(.+)/i,
    /^(.+?)(?:\?|$)/,
  ];

  for (const pattern of patterns) {
    const match = normalized.match(pattern);
    if (match && match[1]) {
      let extracted = match[1].trim();
      extracted = trimFillerPhrases(extracted);
      extracted = takeBeforeDelimiter(extracted);
      if (extracted.length > 2 && !['the', 'a', 'an'].includes(extracted.toLowerCase())) {
        return extracted;
      }
    }
  }

  // Fallback: remove filler and delimiter from entire input, then use if reasonable
  let fallback = trimFillerPhrases(normalized);
  fallback = takeBeforeDelimiter(fallback);
  if (fallback.length > 2 && fallback.length < 100) {
    return fallback;
  }

  return null;
}

// ============================================================================
// Reasoning Badge - Color-coded badges for thought types
// ============================================================================

const BADGE_CONFIG: Record<string, { icon: string; label: string; bg: string; text: string; border: string }> = {
  system: { icon: '⚙️', label: 'System', bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' },
  research: { icon: '🔍', label: 'Research', bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
  analysis: { icon: '📊', label: 'Analysis', bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
  strategic: { icon: '🎯', label: 'Strategic', bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
  technical: { icon: '⚙️', label: 'Technical', bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-300' },
  competitive: { icon: '⚔️', label: 'Competitive', bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' },
};

function ReasoningBadge({ type }: { type: string }) {
  const cfg = BADGE_CONFIG[type] || BADGE_CONFIG.system;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
      <span>{cfg.icon}</span>
      <span>{cfg.label}</span>
    </span>
  );
}

// ============================================================================
// Research Status Panel - Real-time LLM progress with thoughts/reasoning
// ============================================================================

interface ResearchStatusPanelProps {
  currentStep: string;
  progress: number;
  thoughts: Thought[];
  reasoning: Thought[];
  isVisible: boolean;
}

function ResearchStatusPanel({
  currentStep,
  progress,
  thoughts,
  reasoning,
  isVisible
}: ResearchStatusPanelProps) {
  if (!isVisible) return null;

  // Normalize: support both Thought objects and legacy strings
  const normalize = (t: Thought | string): Thought => typeof t === 'string' ? { content: t, type: 'system' } : t;
  const allThoughts = [...thoughts, ...reasoning]
    .map(normalize)
    .filter((t) => t.content);

  return (
    <div className="fixed bottom-24 left-6 right-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg shadow-2xl p-5 max-w-3xl mx-auto z-30">
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0 mt-1">
          <div className="relative w-10 h-10">
            <div className="absolute inset-0 animate-spin rounded-full border-4 border-transparent border-t-blue-600 border-r-blue-400" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-base font-bold text-blue-900 flex items-center gap-2">
              <span className="animate-pulse">🤖</span>
              AI Research in Progress
            </h4>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-blue-700">{progress}%</span>
              <div className="w-16 bg-blue-200 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2.5 mb-4 overflow-hidden">
            <div
              className="bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600 h-2.5 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="mb-4 flex items-center gap-2">
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" />
            <p className="text-sm font-semibold text-blue-900">{currentStep}</p>
          </div>
          {allThoughts.length > 0 && (
            <div className="mt-4 space-y-2 max-h-48 overflow-y-auto">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <p className="text-xs font-bold text-blue-800 uppercase tracking-wider">
                  AI Reasoning &amp; Deep Thinking
                </p>
              </div>
              <div className="space-y-2.5 pl-6">
                {allThoughts.map((thought, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <span className="text-blue-500 text-sm mt-0.5 font-bold">→</span>
                    <div className="flex-1 space-y-1">
                      {thought.type && (
                        <div className="mb-1">
                          <ReasoningBadge type={thought.type} />
                        </div>
                      )}
                      <p className="text-sm text-blue-900 leading-relaxed">{thought.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Inline component definitions (temporary until proper component library is set up)
const LoadingSpinnerInline = ({ text, stage }: { text: string; stage?: string }) => (
  <div className="flex items-center space-x-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
    <div>
      <p className="text-blue-800 font-medium">{text}</p>
      {stage && <p className="text-blue-600 text-sm">{stage}</p>}
    </div>
  </div>
);

const CompanyResearchCardInline = ({
  company_name,
  overview,
  pain_points,
  approach,
  decision_maker,
  timestamp,
}: {
  company_name: string;
  overview: string;
  pain_points: string[];
  approach: string;
  decision_maker: string;
  timestamp?: string;
}) => (
  <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
    <h3 className="text-xl font-bold text-gray-900 mb-4">{company_name} - Research Results</h3>
    <div className="space-y-4">
      <div>
        <h4 className="font-semibold text-gray-700 mb-2">Overview</h4>
        <p className="text-gray-600">{overview}</p>
      </div>
      <div>
        <h4 className="font-semibold text-gray-700 mb-2">Pain Points</h4>
        <ul className="list-disc list-inside space-y-1">
          {pain_points.map((point, i) => (<li key={i} className="text-gray-600">{point}</li>))}
        </ul>
      </div>
      <div>
        <h4 className="font-semibold text-gray-700 mb-2">Recommended Approach</h4>
        <p className="text-gray-600">{approach}</p>
      </div>
      <div>
        <h4 className="font-semibold text-gray-700 mb-2">Target Decision Maker</h4>
        <p className="text-gray-600">{decision_maker}</p>
      </div>
    </div>
  </div>
);

export interface SalesChatAssistantProps {
  /** Pre-fill the input with this company name (e.g. when opened from an account row). */
  prefillCompany?: string | null;
  /** Called when user saves a company to account (optional, for closing modal and refreshing list). */
  onSaveToAccount?: () => void;
}

export function SalesChatAssistant({ prefillCompany, onSaveToAccount }: SalesChatAssistantProps = {}) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [showQuickSetup, setShowQuickSetup] = useState(false);
  const [pendingCompanyName, setPendingCompanyName] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { startResearch, isLoading, insight, progress, currentStep, output, error, uiComponents, thoughts = [], reasoning = [] } = useResearchWorkflow();
  const { checkContext } = useSellerContext();

  useEffect(() => {
    if (prefillCompany && prefillCompany.trim()) {
      setInput(prefillCompany.trim());
    }
  }, [prefillCompany]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = input;
    setInput('');

    // Extract company name from natural language
    const companyName = extractCompanyName(currentInput);

    if (companyName) {
      try {
        const contextCheck = await checkContext();
        if (!contextCheck.exists || !contextCheck.isComplete) {
          setPendingCompanyName(companyName);
          setShowQuickSetup(true);
          return;
        }
      } catch (e) {
        debugLog('Context check failed, proceeding with research:', e);
      }
      await startResearch(companyName);
    } else {
      // Fallback: Ask for clarification
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Which company would you like me to research? Please provide a company name.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMessage]);
    }
  };

  // Update messages when insight is ready
  useEffect(() => {
    if (insight && !isLoading) {
      // Check if we already added a message for this insight
      const hasInsightMessage = messages.some(
        msg => msg.companyData?.id === insight.id
      );
      
      if (!hasInsightMessage) {
        const assistantMessage: Message = {
          id: `insight-${insight.id}`,
          role: 'assistant',
          content: `I've completed the research for ${insight.company_name}. Here's what I found:`,
          timestamp: new Date(),
          companyData: insight,
        };
        setMessages(prev => [...prev, assistantMessage]);
      }
    }
  }, [insight, isLoading, messages]);

  const handleQuickSetupComplete = async () => {
    setShowQuickSetup(false);
    if (pendingCompanyName) {
      await startResearch(pendingCompanyName);
      setPendingCompanyName(null);
    }
  };

  const handleQuickSetupSkip = () => {
    setShowQuickSetup(false);
    if (pendingCompanyName) {
      startResearch(pendingCompanyName);
      setPendingCompanyName(null);
    }
  };

  // Show error message if research fails
  useEffect(() => {
    if (error && !isLoading) {
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: `I encountered an error while researching: ${error}. Please try again with a different company name.`,
        timestamp: new Date(),
      };
      setMessages(prev => {
        // Avoid duplicate error messages
        const hasError = prev.some(msg => msg.id === errorMessage.id);
        return hasError ? prev : [...prev, errorMessage];
      });
    }
  }, [error, isLoading]);

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <h1 className="text-2xl font-bold text-gray-900">Sales Intelligence Assistant</h1>
        <p className="text-sm text-gray-600">Ask me about any company to get instant insights</p>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 pb-32">
        {messages.length === 0 && (
          <div className="text-center text-gray-500 mt-12">
            <h2 className="text-xl font-semibold mb-2">Welcome! 👋</h2>
            <p>Try asking:</p>
            <ul className="mt-4 space-y-2">
              <li className="text-blue-600">"Tell me about Stripe"</li>
              <li className="text-blue-600">"Research HubSpot"</li>
              <li className="text-blue-600">"What do you know about Notion?"</li>
            </ul>
          </div>
        )}

        {messages.map(message => (
          <div key={message.id}>
            <MessageBubble message={message} />
            {message.companyData && (
              <div className="mt-2">
                <CompanyProfileCard data={message.companyData} />
              </div>
            )}
          </div>
        ))}

        {/* Render UI components from SSE stream */}
        {(uiComponents || []).map((uiComponent, index) => {
          const key = uiComponent.id || uiComponent.message_id || `ui-${index}`;
          const raw = (uiComponent.props ?? {}) as Record<string, unknown>;
          const asStr = (v: unknown, fallback = '') => (v == null || v === '' ? fallback : String(v));
          const asStrList = (v: unknown): string[] =>
            Array.isArray(v) ? v.map((x) => String(x)) : [];
          
          switch (uiComponent.component) {
            case 'LoadingSpinner':
              return (
                <div key={key} className="mb-4">
                  <LoadingSpinnerInline
                    text={asStr(raw.text, 'Researching...')}
                    stage={raw.stage != null ? String(raw.stage) : undefined}
                  />
                </div>
              );
              
            case 'CompanyResearchCard':
              return (
                <div key={key} className="mb-4">
                  <CompanyResearchCardInline
                    company_name={asStr(raw.company_name)}
                    overview={asStr(raw.overview)}
                    pain_points={asStrList(raw.pain_points)}
                    approach={asStr(raw.approach)}
                    decision_maker={asStr(raw.decision_maker)}
                    timestamp={String(
                      raw.timestamp ?? uiComponent.timestamp ?? ''
                    )}
                  />
                </div>
              );
              
            case 'ErrorAlert':
              return (
                <div key={key} className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="text-red-800 font-semibold mb-2">Error</h3>
                  <p className="text-red-700">{asStr(raw.message, 'An error occurred')}</p>
                  {raw.details != null && raw.details !== '' && (
                    <p className="text-red-600 text-sm mt-2">{String(raw.details)}</p>
                  )}
                </div>
              );
              
            default:
              debugLog('Unknown UI component:', uiComponent.component);
              return null;
          }
        })}

        {isLoading && uiComponents.length === 0 && (
          <TypingIndicator progress={progress} currentStep={currentStep || 'Researching...'} output={output} />
        )}

        <div ref={messagesEndRef} />
      </div>

      <ResearchStatusPanel
        currentStep={currentStep || 'Initializing...'}
        progress={progress}
        thoughts={thoughts}
        reasoning={reasoning}
        isVisible={isLoading}
      />

      <QuickSetupModal
        isOpen={showQuickSetup}
        onComplete={handleQuickSetupComplete}
        onSkip={handleQuickSetupSkip}
      />

      {/* Input */}
      <div className="bg-white border-t px-6 py-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Ask about any company..."
            className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isLoading}
          />
          <button
            onClick={handleSend}
            disabled={isLoading || !input.trim()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
